import React, { useCallback, useMemo } from 'react';
import { Card, CardText, Media } from 'reactstrap';
import classNames from 'classnames';
import { useHistory } from 'react-router';
import { useMessages } from '@hooks/useMessages';
import { PromoCode } from '@src/@data/types/promoCodes';
import { formatName } from '@src/@data/utility/names';
import { getPath, ROUTE_PROMO_CODES_SECTION } from '@src/@data/router/consts';
import { useIntl } from 'react-intl';

export interface SubscriptionItemProps {
  companyId: string;
  promoCode: PromoCode;
  className?: string;
}

export const SubscriptionItem: React.FC<SubscriptionItemProps> = ({
  companyId,
  promoCode,
  className,
}) => {
  const { messages } = useMessages();
  const history = useHistory();
  const intl = useIntl();

  const dateLabel = useMemo(() => {
    const from = intl.formatDate(promoCode.reservationsFrom);
    const to = intl.formatDate(promoCode.reservationsTill);
    return `${from} - ${to}`;
  }, [intl, promoCode.reservationsFrom, promoCode.reservationsTill]);

  const clientName = useMemo(() => {
    return formatName(promoCode.client?.firstName, promoCode.client?.lastName);
  }, [promoCode.client?.firstName, promoCode.client?.lastName]);

  const goToPromoCode = useCallback(() => {
    history.push(
      getPath(ROUTE_PROMO_CODES_SECTION, {
        companyId,
        promoCodeId: promoCode.id,
      })
    );
  }, [companyId, history, promoCode.id]);

  return (
    <Media
      body
      className={classNames(
        className,
        'align-items-center align-content-center cursor-pointer'
      )}
      onClick={goToPromoCode}
    >
      {!!promoCode.customForm && promoCode.customForm.length > 0 && (
        <Card className="mt-1">
          <CardText className="p-1 whitespace-break-spaces">
            {promoCode.customForm}
          </CardText>
        </Card>
      )}
      {clientName && (
        <div>
          <h6 className="mb-25">
            {messages('promo_codes_description_client')}
          </h6>
          <CardText>{clientName}</CardText>
        </div>
      )}
      <div className="mt-1">
        <h6 className="mb-25">
          {messages('promo_codes_description_active_from')}
        </h6>
        <CardText>{dateLabel}</CardText>
      </div>
      {promoCode.calendarTitle && (
        <div className="mt-1">
          <h6 className="mb-25">
            {messages('promo_codes_description_calendar')}
          </h6>
          <CardText>{promoCode.calendarTitle}</CardText>
        </div>
      )}
      {promoCode.serviceTitle && (
        <div className="mt-1">
          <h6 className="mb-25">
            {messages('promo_codes_description_service')}
          </h6>
          <CardText>{promoCode.serviceTitle}</CardText>
        </div>
      )}
      {!!promoCode.useLimit && promoCode.useLimit > 0 && (
        <div className="mt-1">
          <h6 className="mb-25">
            {messages('promo_codes_description_use_limit')}
          </h6>
          <CardText>
            {promoCode.useCount ?? 0}/{promoCode.useLimit}
          </CardText>
        </div>
      )}
      {!!promoCode.spotsPerUseLimit && promoCode.spotsPerUseLimit > 0 && (
        <div className="mt-1">
          <h6 className="mb-25">
            {messages('promo_codes_spots_per_user_limit')}
          </h6>
          <CardText>{promoCode.spotsPerUseLimit}</CardText>
        </div>
      )}
    </Media>
  );
};

export default SubscriptionItem;
